import { network } from 'services/network';
import { useQuery } from 'react-query';
import { QueryKeys } from 'api/queryKeys';

const queryFn = (params: QueryParams) => {
    return network.post<QueryFnResponse>('api/bo/getActivitiesGraph', params).then((response) => {
        return response.data;
    });
};

export const useQueryActivitiesGraph = (params: QueryParams) => {
    return useQuery<QueryFnResponse, QueryError, QueryResponse, [QueryKeys, unknown]>({
        queryKey: ['activitiesGraph', params],
        queryFn: () => queryFn(params),
        enabled: Boolean(params.id),
        select: (data) => {
            const { activities, versionDate, info } = data;

            const tasks = activities.reduce((acc: TaskWithGroup[], activity, currentIndex) => {
                return acc.concat(
                    activity.tasks.map((task) => {
                        return {
                            ...task,
                            group: currentIndex,
                        };
                    }),
                );
            }, []);

            const tasksConstraints = activities.reduce((acc: Constraints[], item) => {
                const constraints = item.relations.map((r) => {
                    return {
                        id_source: r.sourceTask,
                        id_target: r.targetTask,
                        start_source: r.relationType[0] === 'S',
                        start_target: r.relationType[1] === 'S',
                        relationType: r.relationType,
                    };
                });

                return acc.concat(constraints);
            }, []);

            return {
                info,
                tasksConstraints,
                tasks,
                versionDate,
            };
        },
    });
};
type TaskWithGroup = Task & { group: number };

interface Constraints {
    id_source: Relation['sourceTask'];
    id_target: Relation['targetTask'];
    start_source: boolean;
    start_target: boolean;
    relationType: Relation['relationType'];
}
interface QueryParams {
    id: string;
    maxLevel: number;
    minLevel: number;
}

interface Relation {
    relationType: 'FS' | 'SS' | 'SF' | 'FF';
    sourceTask: string;
    sourceTaskDescription: string;
    targetTask: string;
    targetTaskDescription: string;
}

interface Task {
    activityStatus: string;
    activityType: string;
    actualDurationInDays: number;
    criticalPath: boolean;
    criticalityScore: string;
    delayDriver: boolean;
    description: string;
    durationComplete: number;
    endDate: number;
    milestone: boolean;
    plannedDurationInDays: number;
    predecessors: number;
    startDate: number;
    statusColor: string;
    succseccors: number;
    taskCode: string;
    totalFloatInDays: number | null;
    wbsLevel: number;
    wbsName: string;
}

interface Activity {
    relations: Relation[];
    tasks: Task[];
    wbsCode: string;
    wbsName: string;
}

interface QueryFnResponse {
    activities: Activity[];
    info: IToolTip;
    versionDate: number;
    activityGraphSummary: {
        finishDate: number | null;
        maxWBSLevel: number;
        milestones: number;
        remainingMilestones: number | null;
        remainingTasks: number | null;
        taskActivities: number;
    };
}

interface QueryResponse {
    info: IToolTip;
    tasksConstraints: Constraints[];
    tasks: TaskWithGroup[];
    versionDate: QueryFnResponse['versionDate'];
}
